function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
/**
 * 기본 OutputParser 추상 클래스
 */ export class BaseOutputParser {
    get schema() {
        return this._schema;
    }
    setSchema(schema) {
        this._schema = schema;
    }
    getSchema() {
        return this._schema;
    }
    /**
   * 스키마 기반 데이터 검증
   */ validate(data) {
        if (!this._schema) {
            return {
                success: true,
                data,
                rawOutput: JSON.stringify(data),
                parsedAt: new Date()
            };
        }
        const errors = [];
        const isValid = this.validateValue(data, this._schema, '', errors);
        return {
            success: isValid && errors.length === 0,
            data: isValid ? data : undefined,
            errors: errors.length > 0 ? errors : undefined,
            rawOutput: JSON.stringify(data),
            parsedAt: new Date()
        };
    }
    /**
   * 재귀적 값 검증
   */ validateValue(value, schema, path, errors) {
        // null/undefined 체크
        if (value === null || value === undefined) {
            if (schema.type !== 'null') {
                errors.push({
                    path,
                    message: `Expected ${schema.type}, got null/undefined`,
                    code: 'TYPE_MISMATCH',
                    value
                });
                return false;
            }
            return true;
        }
        // 타입 검증
        if (!this.validateType(value, schema.type)) {
            errors.push({
                path,
                message: `Expected ${schema.type}, got ${typeof value}`,
                code: 'TYPE_MISMATCH',
                value
            });
            return false;
        }
        // 타입별 상세 검증
        switch(schema.type){
            case 'object':
                return this.validateObject(value, schema, path, errors);
            case 'array':
                return this.validateArray(value, schema, path, errors);
            case 'string':
                return this.validateString(value, schema, path, errors);
            case 'number':
                return this.validateNumber(value, schema, path, errors);
            default:
                return true;
        }
    }
    validateType(value, expectedType) {
        switch(expectedType){
            case 'string':
                return typeof value === 'string';
            case 'number':
                return typeof value === 'number' && !isNaN(value);
            case 'integer':
                return typeof value === 'number' && Number.isInteger(value);
            case 'boolean':
                return typeof value === 'boolean';
            case 'array':
                return Array.isArray(value);
            case 'object':
                return typeof value === 'object' && value !== null && !Array.isArray(value);
            case 'null':
                return value === null;
            default:
                return true;
        }
    }
    validateObject(value, schema, path, errors) {
        let isValid = true;
        // 필수 속성 체크
        if (schema.required) {
            for (const requiredProp of schema.required){
                if (!(requiredProp in value)) {
                    errors.push({
                        path: `${path}.${requiredProp}`,
                        message: `Missing required property: ${requiredProp}`,
                        code: 'MISSING_PROPERTY',
                        value: undefined
                    });
                    isValid = false;
                }
            }
        }
        // 각 속성 검증
        if (schema.properties) {
            for (const [propName, propSchema] of Object.entries(schema.properties)){
                if (propName in value) {
                    const propPath = path ? `${path}.${propName}` : propName;
                    if (!this.validateValue(value[propName], propSchema, propPath, errors)) {
                        isValid = false;
                    }
                }
            }
        }
        return isValid;
    }
    validateArray(value, schema, path, errors) {
        let isValid = true;
        if (schema.items) {
            value.forEach((item, index)=>{
                const itemPath = `${path}[${index}]`;
                if (!this.validateValue(item, schema.items, itemPath, errors)) {
                    isValid = false;
                }
            });
        }
        return isValid;
    }
    validateString(value, schema, path, errors) {
        let isValid = true;
        // enum 체크
        if (schema.enum && !schema.enum.includes(value)) {
            errors.push({
                path,
                message: `Value must be one of: ${schema.enum.join(', ')}`,
                code: 'INVALID_ENUM',
                value
            });
            isValid = false;
        }
        return isValid;
    }
    validateNumber(_value, _schema, _path, _errors) {
        // 기본적인 숫자 검증만 구현
        return true;
    }
    /**
   * 파싱 오류 처리 헬퍼
   */ createParseError(message, code, rawOutput) {
        return {
            success: false,
            errors: [
                {
                    path: '',
                    message,
                    code,
                    value: rawOutput
                }
            ],
            rawOutput,
            parsedAt: new Date()
        };
    }
    /**
   * 성공 결과 생성 헬퍼
   */ createSuccessResult(data, rawOutput) {
        return {
            success: true,
            data,
            rawOutput,
            parsedAt: new Date()
        };
    }
    /**
   * 출력에서 코드 블록 추출
   */ extractCodeBlock(output, language) {
        const patterns = [
            // 언어 지정된 코드 블록
            language ? new RegExp(`\`\`\`${language}\\s*([\\s\\S]*?)\`\`\``, 'i') : null,
            // 일반 코드 블록
            /```\w*\s*([\s\S]*?)```/,
            // 인라인 코드
            /`([^`]+)`/
        ].filter(Boolean);
        for (const pattern of patterns){
            const match = output.match(pattern);
            if (match) {
                return match[1].trim();
            }
        }
        return output.trim();
    }
    /**
   * JSON 형태의 텍스트에서 JSON 추출
   */ extractJson(output) {
        // JSON 객체나 배열을 찾는 정규식
        const jsonPattern = /[\{\[][\s\S]*[\}\]]/;
        const match = output.match(jsonPattern);
        if (match) {
            return match[0];
        }
        return output;
    }
    constructor(formatType, schema, options = {}){
        _define_property(this, "_schema", void 0);
        _define_property(this, "options", void 0);
        _define_property(this, "formatType", void 0);
        this.formatType = formatType;
        this._schema = schema;
        this.options = {
            strict: true,
            allowPartial: false,
            maxRetries: 3,
            ...options
        };
    }
}
